import React, { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState, TableSkeleton } from "../../components/EmptyState";
import { DataTable } from "../../components/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { api, apiError } from "../../lib/api";
import { formatINR, formatINRShort, monthLabel } from "../../lib/format";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg">
      <p className="font-medium">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="font-num mt-0.5">
          <span className="text-muted-foreground">{p.name}: </span>
          {formatINR(p.value)}
        </p>
      ))}
    </div>
  );
};

export default function Statement() {
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .get("/api/client/statement")
      .then(({ data }) => {
        if (active) setMonths(data.months || []);
      })
      .catch((error) => toast.error(apiError(error, "Could not load your statement")))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const chartData = useMemo(
    () =>
      months.map((m) => ({
        month: monthLabel(m.month),
        Invested: m.invested,
        Withdrawn: m.withdrawn,
      })),
    [months]
  );

  const totals = useMemo(
    () =>
      months.reduce(
        (acc, m) => ({
          invested: acc.invested + Number(m.invested || 0),
          withdrawn: acc.withdrawn + Number(m.withdrawn || 0),
        }),
        { invested: 0, withdrawn: 0 }
      ),
    [months]
  );

  const columns = useMemo(
    () => [
      { accessorKey: "month", header: "Month", cell: ({ row }) => monthLabel(row.original.month) },
      {
        accessorKey: "invested",
        header: "Invested",
        meta: { numeric: true },
        cell: ({ row }) => formatINR(row.original.invested),
      },
      {
        accessorKey: "withdrawn",
        header: "Withdrawn",
        meta: { numeric: true },
        cell: ({ row }) => formatINR(row.original.withdrawn),
      },
      {
        id: "net",
        header: "Net",
        meta: { numeric: true },
        cell: ({ row }) => {
          const net = Number(row.original.invested || 0) - Number(row.original.withdrawn || 0);
          return (
            <span className={net >= 0 ? "text-[hsl(var(--profit))]" : "text-destructive"}>
              {formatINR(net)}
            </span>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title="Monthly statement"
        subtitle="Capital added against amounts withdrawn, grouped by month."
      />

      {loading ? (
        <TableSkeleton cols={4} />
      ) : months.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No statement data yet"
          description="Once investment or withdrawal entries exist, your monthly statement will be generated automatically."
          testId="statement-empty-state"
        />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total invested</p>
              <p className="font-num mt-2 text-xl font-semibold" data-testid="statement-total-invested">
                {formatINR(totals.invested)}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total withdrawn</p>
              <p className="font-num mt-2 text-xl font-semibold" data-testid="statement-total-withdrawn">
                {formatINR(totals.withdrawn)}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Net position</p>
              <p className="font-num mt-2 text-xl font-semibold text-[hsl(var(--profit))]">
                {formatINR(totals.invested - totals.withdrawn)}
              </p>
            </div>
          </div>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Invested vs withdrawn</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ width: "100%", height: 260 }} data-testid="statement-bar-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      stroke="hsl(var(--border))"
                    />
                    <YAxis
                      tickFormatter={formatINRShort}
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      stroke="hsl(var(--border))"
                      width={72}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--accent))" }} />
                    <Bar dataKey="Invested" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                    <Bar dataKey="Withdrawn" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <DataTable columns={columns} data={months} testId="statement-table" rowTestId="statement-row" />
        </>
      )}
    </div>
  );
}
